import React, { useState, useEffect } from 'react';
import ChangePasswordForm from './components/changePassword';
import EnableOTPForm from './components/enableOTP';
import {
  Typography,
  Box,
  Stack,
  Divider,
  Grid2,
  Avatar,
  Chip,
} from '@mui/material';
import PageLayout from '../../components/layouts/pageLayout';
import TextInput from '../../components/ui/textInput';
import Button from '../../components/ui/button';
import { callAPI } from '../../components/utils';
import Dropdown from '../../components/ui/dropdown';
import Snackbar from '../../components/ui/snackbar';

// Section Header Template
const SectionHeader = ({ title }) => (
  <Stack spacing={1.25}>
    <Typography variant="h4" fontWeight="600">
      {title}
    </Typography>
    <Divider />
  </Stack>
);

const AccountPage = () => {
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openOTPDialog, setOpenOTPDialog] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const defaultUserData = {
    name: 'No name found',
    email: 'No email found',
    phoneNumber: 'No number found',
    gender: '-',
    address: 'Address',
    role: '',
    tutorLevel: 'No level found',
    tutorLevelName: 'No level found',
  };
  const [userData, setUserData] = useState(defaultUserData);
  const [originalUserData, setOriginalUserData] = useState(defaultUserData); // to help with resetting values

  useEffect(() => {
    const getUsers = async () => {
      await callAPI({
        request: 'getUsers',
        method: 'get',
        params: {
          self: true,
        },
        onSuccess: (response) => {
          const user = response.data;
          console.log(response.data);
          const formattedUserData = {
            name: user.name ?? '',
            email: user.email ?? '',
            phoneNumber: user.phoneNumber ?? '',
            gender: user.gender ?? '',
            address: user.address ?? '',
            role: user.roles ?? '',
            tutorLevel: user.level ?? '',
            tutorLevelName: user.levelName ?? '',
          };
          setOriginalUserData(formattedUserData);
          setUserData(formattedUserData);
        },
        onError: (errorMessage) => {
          console.error('Failed to fetch user data:', errorMessage);
        },
      });
    };
    getUsers();
  }, []);

  useEffect(() => {
    const fetchOtpStatus = async () => {
      try {
        const response = await callAPI({
          request: 'checkOtpStatus',
          method: 'get',
        });

        setOtpEnabled(response.data.otpEnabled);
      } catch (error) {
        console.error('Error checking OTP status:', error);
      }
    };

    fetchOtpStatus();
  }, []);

  const handleChange = (field, value) => {
    setUserData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    setUserData(originalUserData);
  };

  // might need to extract this from database later
  const genderOptions = [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Other', value: 'Other' },
  ];

  return (
    <PageLayout title="Settings">
      {/* Account Section */}
      <Grid2 container direction="column" spacing={2}>
        <SectionHeader title="Account" />

        {/* <Stack direction="row" spacing={3} alignItems="center">
          <Avatar
            sx={{
              bgcolor: 'var(--blue-light)',
              color: 'var(--navyBlue-default)',
              width: 50,
              height: 50,
              marginBottom: 1,
            }}
          >
            JS
          </Avatar>
          <Button variant="outlined">Upload Photo</Button>
        </Stack> */}

        {/* User Data Text Inputs */}
        <Stack direction="row" spacing={4} justifyContent="space-evenly">
          <TextInput
            variant="outlined"
            label="Full Name"
            value={userData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            disabled
          />

          <TextInput
            variant="outlined"
            label="Email"
            value={userData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            type="email"
            fullWidth
            disabled
          />

          <TextInput
            variant="outlined"
            label="Phone Number"
            value={userData.phoneNumber}
            onChange={(e) => handleChange('phoneNumber', e.target.value)}
            fullWidth
            disabled
          />
        </Stack>

        <Grid2 container spacing={4} sx={{ display: 'flex', direction: 'row' }}>
          <Grid2 item size={4} sx={{ display: 'flex', direction: 'row' }}>
            <Dropdown
              label="Gender"
              value={userData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              options={genderOptions}
              disabled
            />
          </Grid2>
          <Grid2 item size={4} sx={{ display: 'flex', direction: 'row' }}>
            <TextInput
              variant="outlined"
              label="Address"
              placeholder="Address"
              value={userData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              fullWidth
              disabled
            />
          </Grid2>
          <Grid2 item size={4} sx={{ display: 'flex', direction: 'row' }}>
            <Stack spacing={3} direction="row" sx={{ width: '100%' }}>
              <TextInput
                variant="outlined"
                label="Role"
                value={userData.role}
                fullWidth
                disabled
              />
              <TextInput
                variant="outlined"
                label="Tutor Level"
                value={userData.tutorLevel}
                fullWidth
                disabled
              />
            </Stack>
          </Grid2>
        </Grid2>
      </Grid2>
      {/* <Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {/* Resets values to original user data from database */}
          {/* <Button
            variant="contained"
            textColor="var(--black)"
            bgColor="#F3F4F6"
            onClick={handleCancel}
          >
            Cancel
          </Button> */}
          {/* TO DO: Add this functionality, including success snackbar message */}
          {/* <Button variant="contained">Save Changes</Button>
        </Stack>
      </Box> */}

      {/* Account Security Section */}
      <Grid2 container direction="column" spacing={2}>
        <SectionHeader title="Account Security" />

        {/* Change Password */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h5" fontWeight="600">
              Password
            </Typography>
            <Typography variant="body1">
              Change your password regularly for security
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => setOpenChangePassword(true)}
          >
            Change Password
          </Button>
        </Stack>
        <ChangePasswordForm
          open={openChangePassword}
          onClose={() => setOpenChangePassword(false)}
          otpEnabled={otpEnabled}
          onSuccess={() => {
            setOpenChangePassword(false);
            setSnackbarOpen(true);
            setSuccessMessage('Password changed successfully');
          }}
        />

        {/* Enable OTP */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5" fontWeight="600">
                Two-Factor Authentication
              </Typography>
              {otpEnabled && (
                <Chip
                  label="Enabled"
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: '#E0F2FE',
                    color: '#60A5FA',
                  }}
                />
              )}
            </Stack>
            <Typography variant="body1">
              Add an additional layer of security to your account during login
            </Typography>
          </Box>
          <Button
            variant={otpEnabled ? 'contained' : 'outlined'}
            textColor={otpEnabled ? 'var(--red-600)' : 'var(--black)'}
            bgColor={otpEnabled ? '#FEEAEA' : '#FDA041'}
            onClick={() => setOpenOTPDialog(true)}
          >
            {otpEnabled ? 'Disable Verification' : 'Set Up Verification'}
          </Button>
        </Stack>
        <EnableOTPForm
          open={openOTPDialog}
          onClose={() => setOpenOTPDialog(false)}
          otpEnabled={otpEnabled}
          onSuccess={() => {
            setOpenOTPDialog(false);
            setOtpEnabled(!otpEnabled);
            setSnackbarOpen(true);
            otpEnabled
              ? setSuccessMessage(
                  'Two-factor authentication disabled successfully'
                )
              : setSuccessMessage(
                  'Two-factor authentication enabled successfully'
                );
          }}
        />
      </Grid2>

      {/* Snack bar for success messages */}
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        type="success"
        message={successMessage}
        duration={4000}
      />
    </PageLayout>
  );
};

export default AccountPage;
